<script>
import ValidationMixin from "@/mixins/validation.mixin";
import GoogleMapsMixin from "@/mixins/google-maps.mixin";
import OmniTextarea from "@/components/inputs/Textarea.vue";
import StopDateRange from "@/views/orders/components/StopDateRange.vue";
import OmniDialog from "@/components/OmniDialog.vue";
import { customAlphabet } from "nanoid";
import { OrderFreightState, OrderStopType } from "@/data/order";
import AlertMessage from "@/components/AlertMessage.vue";
import { RouteStopStatus } from "@/data/trip";
import { useOrder } from "@/composables/index.js";
import PickUpReloadFreights from "@/views/trips/edit/blocks/PickUpReloadFreights.vue";
import RouteStopFreight from "@/views/trips/edit/blocks/RouteStopFreight.vue";
import DeletedChip from "@/components/chips/DeletedChip.vue";

const { isReloadStop, getFreightName, timeTypesMap, getStopTime } = useOrder();

export default {
  name: "EditStopDialog",
  components: {
    DeletedChip,
    RouteStopFreight,
    PickUpReloadFreights,
    AlertMessage,
    OmniDialog,
    StopDateRange,
    OmniTextarea,
  },
  mixins: [ValidationMixin, GoogleMapsMixin],
  props: {
    modelValue: Boolean,
    stop: { type: Object, required: true },
    tripStops: { type: Array, required: true },
    routeFreights: { type: Array, required: true },
    orderFreights: { type: Array, required: true },
    allowEditUsage: {
      type: Boolean,
      default: false,
    },
    routes: {
      type: Array,
      required: true,
    },
    route: {
      type: Object,
      required: true,
    },
    initialRouteStopFreights: {
      type: Array,
      required: true,
    },
    routeStopFreightsFromApi: {
      type: Array,
      required: true,
    },
    stopType: {
      type: String,
      required: true,
    },
  },
  emits: ["update:modelValue", "changed"],
  data() {
    return {
      updatedStopFreights: [],
      stopData: {},
    };
  },
  computed: {
    timeTypesMap() {
      return timeTypesMap;
    },
    OrderFreightState() {
      return OrderFreightState;
    },
    showDialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    tripStop() {
      return this.tripStops.find((os) => os.id === this.stop.tripStopId);
    },
    facilityAddress() {
      const chunks = [];
      if (this.stop.facility.addressLine) {
        chunks.push(this.stop.facility.addressLine);
      }
      if (this.stop.facility.address) {
        chunks.push(this.stop.facility.address);
      }
      return chunks.join(", ");
    },
    freightOptions() {
      const orderFreightsWithName = this.orderFreights.map((orderFreight) => {
        return {
          ...orderFreight,
          name: getFreightName(orderFreight, orderFreight.number, "", false),
        };
      });

      if (this.isReload) {
        return orderFreightsWithName;
      }

      return orderFreightsWithName.filter((f) => this.tripStop.freights.includes(f.id));
    },
    isPickup() {
      return this.stopType === OrderStopType.pickup;
    },
    isDelivery() {
      return this.stopType === OrderStopType.delivery;
    },
    isReload() {
      return isReloadStop(this.stop);
    },
    changeDisabled() {
      return (
        (this.isPickup && ![RouteStopStatus.notStarted, RouteStopStatus.inRoute].includes(this.stop.status)) ||
        !!(this.stop.isDeleted && this.stop?.id)
      );
    },
    saveDisabled() {
      return (this.isPickup || (this.isReload && this.isDelivery)) && this.updatedStopFreights.length === 0;
    },
    primaryActionButton() {
      if (!this.allowEditUsage) {
        return this.stop.use ? "Save" : "Add";
      }

      if (this.stop?.id) {
        return this.stop.isDeleted ? null : "Save";
      }

      return this.stop.use ? "Save" : "Add";
    },
    secondaryActionLabel() {
      if (this.stop?.id) {
        return this.stop.isDeleted ? "Undo delete" : "Delete stop";
      }
      return this.stop.use ? "Delete stop" : null;
    },
    routeStops() {
      return this.route.routeStops;
    },
    secondaryButtonDisabled() {
      if (!this.routeStops.length) {
        return true;
      }

      const validStops = this.routeStops.filter(
        (stop) => (stop.type === OrderStopType.notSelected || stop.type === this.stopType) && !stop.isDeleted,
      );

      return validStops.length <= 1;
    },
    secondaryTooltipText() {
      if (!this.secondaryButtonDisabled) {
        return "";
      }

      return "The stop cannot be deleted because the route must contain at least 1 Pick up and 1 Delivery stop";
    },
    title() {
      return this.stop.isReload ? `${this.stop.name} reload stop settings for` : `Edit route stop ${this.stop.name}`;
    },
  },
  watch: {
    modelValue: {
      handler(value) {
        if (!value) {
          this.updatedStopFreights = [];
          this.stopData = {};
        } else {
          this.updatedStopFreights = [...this.initialRouteStopFreights];

          this.stopData = {
            timeType: this.stop.timeType,
            timezone: this.stop.timezone,
            timeFrom: this.stop.timeFrom,
            timeTo: this.stop.timeTo,
            time2Type: this.stop.time2Type,
            time2From: this.stop.time2From,
            time2To: this.stop.time2To,
            type: this.stopType,
          };
        }
      },
      immediate: true,
    },
  },
  methods: {
    getFreightNameFunction(...arg) {
      return getFreightName(...arg);
    },
    getStopTimeFunction(time) {
      return getStopTime(time);
    },
    getOrderStopsByFreight(freight) {
      return this.tripStops.filter((os) => {
        return os.freights?.includes(freight?.id);
      });
    },
    getOrderByFreight(freight) {
      return this.getOrderStopsByFreight(freight)[0].order;
    },
    getFreightPoints(freight) {
      const tripStops = this.getOrderStopsByFreight(freight);
      return tripStops.map((os) => ({ ...os.facility, fullAddress: os.facility.address }));
    },
    getRouteFreightsForOrderFreight(orderFreight) {
      return this.routeFreights
        .filter((rf) => rf.freightId === orderFreight.id)
        .map((rf, i) => {
          return {
            ...rf,
            number: i + 1,
          };
        });
    },
    getStopRouteFreightsForOrderFreight(orderFreight) {
      if (this.isReload && this.stopType === OrderStopType.pickup) {
        return this.tripStops
          .find((tripStop) => tripStop.id === this.stop.orderStopId)
          ?.reloadFreights.filter((reloadFreight) => reloadFreight.freightId === orderFreight.id);
      }

      return this.updatedStopFreights
        .filter((rf) => rf.freightId === orderFreight.id)
        .map((rf, i) => {
          rf.number = i + 1;
          return rf;
        });
    },
    addFreight(orderFreight) {
      const state = this.route.id ? OrderFreightState.new : OrderFreightState.normal;

      this.updatedStopFreights.push({
        id: this.generateId(),
        freightId: orderFreight.id,
        quantity: null,
        weight: null,
        stackable: orderFreight.stackable,
        type: orderFreight.type,
        plannedWeight: 1,
        plannedQuantity: 1,
        state,
        $isNew: true,
      });
    },
    isSelected(routeFreight) {
      return this.updatedStopFreights.map((rf) => rf.id).includes(routeFreight.id);
    },
    selectFreight(routeFreight, value) {
      if (value) {
        this.updatedStopFreights.push(routeFreight);
      } else {
        this.updatedStopFreights = this.updatedStopFreights.filter((rf) => rf.id !== routeFreight.id);
      }
    },
    generateId() {
      const nanoid = customAlphabet("1234567890abcdef", 16);
      return nanoid();
    },
    hasActiveDeliveryForFreight(routeFreight) {
      return this.routeStops.some((routeStop) => {
        return (
          routeStop.type === OrderStopType.delivery &&
          routeStop.id !== this.stop.id &&
          routeStop.freights.includes(routeFreight.id) &&
          !routeStop.isDeleted
        );
      });
    },
    checkIsFreightDisabled(routeFreight) {
      return this.stop.checkInTime || this.hasActiveDeliveryForFreight(routeFreight) || routeFreight.isDeleted;
    },
    getUpdatedRouteFreights(isDeleteStop) {
      const stopFreightIds = new Set(this.stop.freights);
      const updateStopFreightsById = new Map(this.updatedStopFreights.map((f) => [f.id, f]));

      if (isDeleteStop && !this.stop.id && this.isPickup) {
        return this.route.freights.filter((f) => !stopFreightIds.has(f.id));
      }

      let baseFreights = this.route.freights;

      if (this.isPickup) {
        baseFreights = baseFreights.filter((freight) => {
          const isRelatedToStop = stopFreightIds.has(freight.id);
          return !(isRelatedToStop && !updateStopFreightsById.has(freight.id));
        });
      }
      const updatedFreights = baseFreights.map((freight) => {
        const updated = updateStopFreightsById.get(freight.id);
        return updated ? { ...freight, ...updated } : freight;
      });

      const existingIds = new Set(updatedFreights.map((f) => f.id));
      const newFreights = this.updatedStopFreights.filter((f) => !existingIds.has(f.id));

      return [...updatedFreights, ...newFreights];
    },
    async save(isDeleted = false, use = true) {
      const { valid } = await this.$refs.editStopForm.validate();
      if (!valid) return;

      const stop = {
        ...this.stop,
        ...this.stopData,
        type: this.stopType,
        use,
        isDeleted: isDeleted,
        freights: this.updatedStopFreights.map((f) => f.id),
      };

      this.$emit("changed", stop, this.getUpdatedRouteFreights(isDeleted));
      this.showDialog = false;
    },
    remove() {
      this.save(!this.stop?.isDeleted, Boolean(this.stop.id));
    },
  },
};
</script>

<template>
  <omni-dialog
    v-model="showDialog"
    :disabled="saveDisabled"
    :primary-action-label="primaryActionButton"
    :secondary-action-label="secondaryActionLabel"
    :secondary-button-disabled="secondaryButtonDisabled"
    :secondary-tooltip-text="secondaryTooltipText"
    :title="title"
    has-secondary-button-tooltip
    width="1000"
    @primary-action:click="save"
    @secondary-action:click="remove"
  >
    <template v-if="stop.isReload && route.truck?.id" #title:append>
      <router-link
        :to="{ name: 'truck-view', params: { id: route.truck?.id } }"
        class="text-primary text-decoration-none ml-1"
        target="_blank"
      >
        Truck#{{ route.truck.number }}
      </router-link>
    </template>
    <v-form ref="editStopForm">
      <div class="mb-4">
        <v-text-field
          :model-value="stop.facility.name"
          class="required"
          density="compact"
          hide-details
          label="Facility"
          readonly
          variant="outlined"
        />
      </div>
      <div class="bg-grey-lighten-5 pa-3 rounded mb-6">
        <div class="text-grey-darken-3 font-size-16 mb-2">{{ facilityAddress }}</div>
        <div class="d-flex align-center mb-6">
          <v-icon class="mr-2" color="green" size="16" small> mdi-map-marker-radius-outline</v-icon>
          <a
            :href="getPlaceUrl(stop.facility.addressCoordinates)"
            class="text-green caption text-decoration-underline"
            target="_blank"
          >
            Lat: {{ stop.facility.addressCoordinates.lat }} Lon: {{ stop.facility.addressCoordinates.lon }}
          </a>
        </div>
        <omni-textarea label="Facility note" readonly rows="2" variant="filled"></omni-textarea>
      </div>

      <div class="mb-6">
        <pick-up-reload-freights
          v-if="isReload && isPickup"
          v-model="updatedStopFreights"
          :freights-from-api="routeStopFreightsFromApi"
          :routes="routes"
          :stop="stop"
          :trip-stops="tripStops"
        />
        <template v-else>
          <div class="text-grey-darken-3 font-size-16 font-weight-500">Freight(s) from order:</div>
          <div
            v-for="orderFreight in freightOptions"
            :key="orderFreight.id"
            :class="{
              'freight--deleted': orderFreight.isDeleted,
              'freight--new': orderFreight.state === OrderFreightState.new,
              'freight--updated': orderFreight.state === OrderFreightState.updated,
            }"
            class="freight mt-4 rounded-b"
          >
            <div class="freight--header bg-grey-lighten-4 pa-4">
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="d-flex align-center">
                  <div class="text-grey-darken-3 font-weight-500 font-size-16 freight--name mr-1">
                    {{ orderFreight.name }}
                  </div>
                  <span v-if="orderFreight.isDeleted" class="text-grey font-weight-500 ml-4">
                    Order #{{ getOrderByFreight(orderFreight)?.number }}
                  </span>
                  <router-link
                    v-else
                    :to="{ name: 'order-view', params: { id: getOrderByFreight(orderFreight)?.id } }"
                    class="text-decoration-none text-primary font-weight-500 ml-4"
                    target="_blank"
                  >
                    Order #{{ getOrderByFreight(orderFreight)?.number }}
                  </router-link>
                </div>

                <deleted-chip v-if="orderFreight.isDeleted" />
              </div>

              <div class="text-grey-darken-1 d-flex align-center freight--points">
                <template v-for="(point, i) in getFreightPoints(orderFreight)" :key="point.id">
                  {{ point.fullAddress }}
                  <v-icon
                    v-if="i < getFreightPoints(orderFreight).length - 1"
                    class="mx-1"
                    icon="mdi-arrow-right"
                    size="14"
                  ></v-icon>
                </template>
              </div>

              <alert-message
                v-if="orderFreight.isDeleted"
                bg-color="indigo-lighten-5"
                border-color="none"
                class="mt-4"
                color="indigo"
                icon="mdi-information-outline"
                icon-color="indigo"
              >
                Freight#{{ orderFreight.number }} will be removed after re-dispatch confirmation
              </alert-message>
            </div>

            <div v-if="isPickup" class="bg-grey-lighten-5 freight--form pa-4 freight-border rounded-b">
              <alert-message v-if="orderFreight.state === OrderFreightState.updated" class="mb-4">
                Please update route freight information
              </alert-message>
              <div class="text-grey-darken-2 mb-4 freight--add-label font-weight-500">
                Add route freight for order freight:
              </div>

              <template
                v-for="routeFreight in getStopRouteFreightsForOrderFreight(orderFreight)"
                :key="routeFreight.id"
              >
                <route-stop-freight
                  v-model="updatedStopFreights"
                  :freights-from-api="routeStopFreightsFromApi"
                  :initial-route-stop-freights="initialRouteStopFreights"
                  :route-freight="routeFreight"
                  :route-stop="stop"
                />
              </template>

              <v-btn
                :disabled="orderFreight.isDeleted || changeDisabled"
                class="px-2 mt-4"
                data-qa="add-route-freight-button"
                density="compact"
                variant="text"
                @click="addFreight(orderFreight)"
              >
                <div class="d-flex align-center text-primary text-uppercase font-size-11 font-weight-600">
                  <v-icon class="mr-2" icon="mdi-plus"></v-icon>
                  Add route freight
                </div>
              </v-btn>
            </div>
            <div v-if="isDelivery" class="bg-grey-lighten-5 pa-4 rounded-b freight-border">
              <div class="text-grey-darken-2 mb-4">Select route freight(s) to be delivered to this stop:</div>
              <div v-for="routeFreight in getRouteFreightsForOrderFreight(orderFreight)" :key="routeFreight.id">
                <v-tooltip :disabled="!checkIsFreightDisabled(routeFreight)" location="top left">
                  <template #activator="{ props: tooltipProps }">
                    <div v-bind="tooltipProps">
                      <div class="d-flex align-center">
                        <v-checkbox
                          :disabled="checkIsFreightDisabled(routeFreight)"
                          :label="
                            'Route freight ' +
                            getFreightNameFunction(routeFreight, routeFreight.number, '', false, true)
                          "
                          :model-value="isSelected(routeFreight)"
                          color="primary"
                          data-qa="select-route-freight"
                          density="compact"
                          hide-details
                          @update:model-value="(val) => selectFreight(routeFreight, val)"
                        ></v-checkbox>

                        <deleted-chip v-if="routeFreight.isDeleted" class="ml-3" />
                      </div>
                    </div>
                  </template>

                  This freight can’t be selected on this stop
                </v-tooltip>

                <alert-message v-if="!isSelected(routeFreight)" class="my-2">
                  Select Route freight#{{ routeFreight.number }} at least at one DEL
                </alert-message>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div>
        <div class="d-flex align-center text-grey-darken-1 font-weight-500 mb-4">
          <v-icon class="mr-2" icon="mdi-clock-outline" size="16"></v-icon>
          {{ stop.isReload ? "Trip reload" : "Order" }} stop time: {{ timeTypesMap[stop.timeType] }}:
          {{ getStopTimeFunction(stop) }}
        </div>
        <stop-date-range
          v-model="stopData"
          :disabled="changeDisabled"
          :hide2-time-frame="stop.isReload"
          multiline
        ></stop-date-range>
      </div>
    </v-form>
  </omni-dialog>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.freight {
  &.freight--deleted {
    .freight--header,
    .freight--form {
      background-color: $grey-input-bg !important;
    }

    .freight--name,
    .freight--points,
    .freight--add-label,
    .freight--route-freight-name {
      color: $grey-lighter-contrast !important;
    }

    .freight--name {
      position: relative;

      &:after {
        content: "";
        position: absolute;
        width: 6px;
        height: 6px;
        display: block;
        top: 50%;
        right: -12px;
        border-radius: 50%;
        transform: translateY(-50%);
        text-decoration: none !important;
        background-color: $grey-light;
      }
    }
  }

  &.freight--new {
    border: 1px solid $green;

    .freight--header {
      background-color: $green-lighten !important;
    }

    .freight--name {
      position: relative;

      &:after {
        position: absolute;
        content: "•";
        font-size: 20px;
        text-decoration: none !important;
        color: $green;
        top: 50%;
        transform: translateY(-50%);
        right: -12px;
      }
    }
  }

  &.freight--updated {
    border: 1px solid $warning;

    .freight--header {
      background-color: $warning-light !important;
    }

    .freight--name {
      position: relative;

      &:after {
        position: absolute;
        content: "•";
        font-size: 20px;
        text-decoration: none !important;
        color: $warning;
        top: 50%;
        transform: translateY(-50%);
        right: -12px;
      }
    }
  }

  &-border {
    border-left: 1px solid #e0e0e0;
    border-right: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
  }
}
</style>
